import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { X, ZoomIn, ZoomOut, RotateCcw, ChevronLeft, ChevronRight } from "lucide-react";
import { asset } from "@/lib/api";
import { lenisRef } from "@/lib/lenis";

const MAX = 6;
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

const Ctrl = ({ testid, label, onClick, children }) => (
  <button data-testid={testid} onClick={onClick} aria-label={label}
    className="w-11 h-11 grid place-items-center bg-black/70 border border-[#F5A524]/40 text-[#F5A524] hover:bg-[#F5A524] hover:text-black active:scale-95 transition-[background-color,color,transform]">
    {children}
  </button>
);

export const ZoomViewer = ({ images, start = 0, title, titles, onClose }) => {
  const [i, setI] = useState(start);
  const [view, setView] = useState({ s: 1, x: 0, y: 0 });
  const [live, setLive] = useState(false);
  const stage = useRef(null);
  const pts = useRef(new Map());
  const gesture = useRef(null);
  const lastTap = useRef(0);
  const n = images.length;
  const label = titles?.[i] || title;

  const reset = () => setView({ s: 1, x: 0, y: 0 });
  const go = useCallback((d) => { setI((x) => (x + d + n) % n); setView({ s: 1, x: 0, y: 0 }); }, [n]);
  const zoomBy = (f) => setView((v) => { const s = clamp(v.s * f, 1, MAX); return s === 1 ? { s, x: 0, y: 0 } : { ...v, s }; });

  useEffect(() => {
    lenisRef.current?.stop();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && n > 1) go(1);
      if (e.key === "ArrowLeft" && n > 1) go(-1);
      if (e.key === "+" || e.key === "=") zoomBy(1.25);
      if (e.key === "-") zoomBy(0.8);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      lenisRef.current?.start();
    };
  }, [onClose, go, n]);

  useEffect(() => {
    const el = stage.current;
    const onWheel = (e) => {
      e.preventDefault();
      zoomBy(e.deltaY < 0 ? 1.15 : 1 / 1.15);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const onDown = (e) => {
    stage.current.setPointerCapture(e.pointerId);
    pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const list = [...pts.current.values()];
    setLive(true);
    if (list.length === 2) {
      gesture.current = { type: "pinch", d: Math.hypot(list[0].x - list[1].x, list[0].y - list[1].y), s: view.s };
    } else {
      gesture.current = { type: "pan", x: e.clientX, y: e.clientY, vx: view.x, vy: view.y, t: Date.now() };
    }
  };

  const onMove = (e) => {
    if (!pts.current.has(e.pointerId)) return;
    pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    const list = [...pts.current.values()];
    if (g?.type === "pinch" && list.length === 2) {
      const d = Math.hypot(list[0].x - list[1].x, list[0].y - list[1].y);
      setView((v) => ({ ...v, s: clamp((g.s * d) / g.d, 1, MAX) }));
    } else if (g?.type === "pan" && view.s > 1) {
      setView((v) => ({ ...v, x: g.vx + (e.clientX - g.x), y: g.vy + (e.clientY - g.y) }));
    }
  };

  const onUp = (e) => {
    const g = gesture.current;
    pts.current.delete(e.pointerId);
    if (pts.current.size > 0) return;
    setLive(false);
    gesture.current = null;
    if (g?.type !== "pan") {
      if (view.s <= 1.02) reset();
      return;
    }
    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;
    const moved = Math.hypot(dx, dy) > 8;
    if (!moved) {
      const now = Date.now();
      if (now - lastTap.current < 300) {
        setView((v) => (v.s > 1 ? { s: 1, x: 0, y: 0 } : { s: 2.5, x: 0, y: 0 }));
        lastTap.current = 0;
      } else lastTap.current = now;
    } else if (view.s === 1 && n > 1 && Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
      go(dx < 0 ? 1 : -1);
    }
  };

  return createPortal(
    <motion.div data-testid="zoom-viewer" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[200] bg-[#05060A]/95 backdrop-blur-sm flex flex-col" data-lenis-prevent>
      <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 font-mono text-[11px] tracking-[0.2em]">
        <span className="text-[#F5A524] truncate">{label}{n > 1 && <span className="text-zinc-400"> // {String(i + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}</span>}</span>
        <div className="flex gap-2 shrink-0">
          <Ctrl testid="zoom-out-btn" label="Zoom out" onClick={() => zoomBy(0.8)}><ZoomOut size={18} /></Ctrl>
          <span data-testid="zoom-level" className="hidden sm:grid place-items-center w-14 text-zinc-300">{Math.round(view.s * 100)}%</span>
          <Ctrl testid="zoom-in-btn" label="Zoom in" onClick={() => zoomBy(1.25)}><ZoomIn size={18} /></Ctrl>
          <Ctrl testid="zoom-reset-btn" label="Reset zoom" onClick={reset}><RotateCcw size={16} /></Ctrl>
          <Ctrl testid="zoom-close-btn" label="Close" onClick={onClose}><X size={20} /></Ctrl>
        </div>
      </div>
      <div ref={stage} data-testid="zoom-stage" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
        className={`relative flex-1 min-h-0 overflow-hidden select-none ${view.s > 1 ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"}`}
        style={{ touchAction: "none" }}>
        <img data-testid="zoom-image" src={asset(images[i])} alt={label} draggable={false}
          className="absolute inset-0 w-full h-full p-2 sm:p-4 object-contain pointer-events-none"
          style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.s})`, transition: live ? "none" : "transform 0.25s ease-out" }} />
        {n > 1 && (
          <>
            <div className="absolute left-3 top-1/2 -translate-y-1/2" onPointerDown={(e) => e.stopPropagation()}><Ctrl testid="zoom-prev-btn" label="Previous image" onClick={() => go(-1)}><ChevronLeft /></Ctrl></div>
            <div className="absolute right-3 top-1/2 -translate-y-1/2" onPointerDown={(e) => e.stopPropagation()}><Ctrl testid="zoom-next-btn" label="Next image" onClick={() => go(1)}><ChevronRight /></Ctrl></div>
          </>
        )}
      </div>
      <p className="text-center py-3 font-mono text-[11px] tracking-[0.25em] text-zinc-400">PINCH OR SCROLL TO ZOOM · DOUBLE-TAP TO ZOOM · DRAG TO PAN · ESC TO CLOSE</p>
    </motion.div>,
    document.body,
  );
};
